import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {getTranslation} from "../../utils/translationUtil";
import {ResearchFilterDropdown} from "./ResearchFilterDropdown.component";
import {ResearchActiveFilters} from "./ResearchActiveFilters.component";
import {IUseResearchFiltersResult} from "./useResearchFilters";
import {exportResearchToCsv} from "../../utils/researchUtil";
import {ProductSummary} from "../../model/product/ProductSummary";
import {FILTER_FIELD_LABELS, FILTER_FIELD_DEFAULTS} from "../../model/ResearchPageState.model";

export interface IResearchFilterBarProps {
    filters: IUseResearchFiltersResult;
    filteredData: ProductSummary[];
    onOpenRatingDefinitions?: () => void;
    onExportCsv?: () => void;
}

export const ResearchFilterBar: React.FunctionComponent<IResearchFilterBarProps> = (props) => {
    const searchInputRef = useRef<HTMLInputElement>(null);
    const {searchText, setSearchText, filterFields} = props.filters;
    const [localSearch, setLocalSearch] = useState(searchText);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    const handleDropdownOpenChange = useCallback((field: string, open: boolean) => {
        setOpenDropdown((prev) => {
            if (open) return field;
            return prev === field ? null : prev;
        });
    }, []);

    useEffect(() => {
        if (!openDropdown) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Element;
            if (!target.closest?.(".research-filter-dropdown")) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openDropdown]);

    useEffect(() => {
        setLocalSearch(searchText);
    }, [searchText]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localSearch !== searchText) {
                setSearchText(localSearch);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [localSearch, searchText, setSearchText]);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalSearch(e.target.value);
    }, []);

    const handleExportCsv = useCallback(() => {
        if (props.onExportCsv) {
            props.onExportCsv();
        } else {
            exportResearchToCsv(props.filteredData as unknown as Record<string, unknown>[]);
        }
    }, [props.filteredData, props.onExportCsv]);

    const filterToolbarLabel = getTranslation("research-page.filter-toolbar", "Research filters");

    return (
        <div role="search" aria-label={filterToolbarLabel} className="research-filter-bar__wrapper">
            <div className="research-filter-bar">
                <div className="research-filter-bar__filter-label" aria-hidden="true">
                    <i className="fa-regular fa-bars-filter" />
                    <span>{getTranslation("research-page.filter-label", "Filters")}</span>
                </div>
                <div className="research-filter-bar__search-wrapper">
                    <i className="fa-solid fa-magnifying-glass research-filter-bar__search-icon" aria-hidden="true" />
                    <label htmlFor="research-search-input" className="sr-only">
                        {getTranslation("research-page.search-label", "Search strategies")}
                    </label>
                    <input
                        id="research-search-input"
                        ref={searchInputRef}
                        type="search"
                        className="research-filter-bar__search-input"
                        placeholder={getTranslation("research-page.search-placeholder", "Search all fields...")}
                        value={localSearch}
                        onChange={handleSearchChange}
                    />
                </div>

                {filterFields.map((field) => {
                    const labelKey = FILTER_FIELD_LABELS[field] || field;
                    const labelDefault = FILTER_FIELD_DEFAULTS[field] || field;
                    const label = getTranslation(labelKey, labelDefault);
                    const options = props.filters.facetedCounts[field] || [];
                    const selected = props.filters.facetedFilters[field] ?? new Set<string>();
                    return (
                        <ResearchFilterDropdown
                            key={field}
                            label={label}
                            field={field}
                            options={options}
                            selectedValues={selected}
                            onToggle={props.filters.toggleFilterValue}
                            isOpen={openDropdown === field}
                            onOpenChange={(open) => handleDropdownOpenChange(field, open)}
                        />
                    );
                })}

                <div className="research-filter-bar__spacer" />

                <div className="research-filter-bar__toolbar-actions">
                    {props.onOpenRatingDefinitions && (
                        <button
                            type="button"
                            className="research-filter-bar__rating-link"
                            onClick={props.onOpenRatingDefinitions}
                            aria-label={getTranslation("research-page.rating.definitions", "Rating Definitions")}
                        >
                            {getTranslation("research-page.rating.definitions", "Rating Definitions")}
                        </button>
                    )}
                    <button
                        type="button"
                        className="research-filter-bar__csv-button"
                        onClick={handleExportCsv}
                        aria-label={getTranslation("research-page.export-csv", "Export to CSV")}
                    >
                        <i className="fa-solid fa-download" aria-hidden="true" />
                        {getTranslation("research-page.csv-label", "CSV")}
                    </button>
                </div>
            </div>

            <ResearchActiveFilters
                facetedFilters={props.filters.facetedFilters}
                searchText={props.filters.searchText}
                onRemoveFilter={props.filters.removeFilterValue}
                onClearAll={props.filters.clearAllFilters}
            />
        </div>
    );
};
